import React from 'react';
import PropTypes from 'prop-types';
import { InstantSearch, SearchBox, Configure } from 'react-instantsearch/dom';
import omit from 'lodash/omit';
import qs from 'qs';
import { Modes, AlgoliaConstants } from '../common/Constants';
import { defaultOrganisationListingLogoUrl } from '../../images/assets.json';
import TranslationService from '../translations';
import Tags from '../common/dtmTags';
import ProductDirectoryWithConnection from './ProductDirectoryWithConnection';
import Classes from '../common/Classes';
import SearchResultsWithConnection from '../directory/SearchResults';
import ExhibitorTotal from '../directory/ExhibitorTotalWithConnection';

const searchStateToUrl = (searchState) => {
  // avoiding algolia configurations from getting added to URL
  const { configure, ...state } = searchState;
  const searchStateStringified = qs.stringify(state);
  return (searchState ? `${window.location.pathname}?${searchStateStringified}` : '');
};

const refineSearchState = (searchStateQuery) => {
  const parsedQuery = qs.parse(searchStateQuery);
  return omit(parsedQuery, 'page');
};


class ProductDirectory extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchState: refineSearchState(window.location.search.slice(1))
    };
  }

  onSearchStateChange = (searchState) => {
    this.setState({ searchState });
    clearTimeout(this.deboundedState);
    this.deboundedState = setTimeout(() => {
      window.history.replaceState(
        searchState,
        null,
        searchStateToUrl(searchState)
      );
    }, 500);
  };

  getIndexName = () => {
    return `${this.props.context.eventId}-index`
  }

  getConfiguration = () => (<Configure
    hitsPerPage={AlgoliaConstants.hitsPerPage}
    restrictSearchableAttributes={['name', 'description', 'exhibitingOrganisationName']}
  />);

  renderInstantSearchWidget = ({
    translations, navigation, testVariants
  }) => (
    <div>
      <div data-dtm={Tags.productDirectory.search} className={Classes.productSearch}>
        <SearchBox
          translations={{
            placeholder: translations.directory.searchExhibitorPlaceholder,
            submitTitle: translations.directory.searchButton,
            resetTitle: translations.directory.clearSearch
          }}
        />
        <ExhibitorTotal defaultTitle={translations.productDirectory.products} tags={Tags.productDirectory} />
      </div>
      <div className={Classes.textCenter}>
        <SearchResultsWithConnection translations={translations.productDirectory} />
      </div>
      <div data-dtm={Tags.productDirectory.products.section}>
        <ProductDirectoryWithConnection
          translations={translations}
          defaultOrganisationListingLogoUrl={defaultOrganisationListingLogoUrl}
          exhibitorPublicDetailsUrlFormat={navigation.exhibitorPublicDetailsUrlFormat}
          onTagClick={this.onTagClick}
          testVariants={testVariants}
          showPlanningEnabled={this.props.context.showPlanningEnabled}
          isRounded
        />
      </div>
    </div>
  );

  render() {
    const {
      algoliaConfig = {}, context, assets, navigation, testVariants
    } = this.props;
    const { searchState, filterGroupNames, showPlan } = this.state;
    const translations = TranslationService.getTranslations(context.interfaceLocale);

    const indexName = this.getIndexName();
    const configurationDOM = this.getConfiguration();
    const searchWidgetDOM = this.renderInstantSearchWidget({
      translations, filterGroupNames, assets, navigation, testVariants, showPlan
    });

    return (
      <div data-dtm={Tags.productDirectory.component}>
        <InstantSearch
          appId={algoliaConfig.appId}
          apiKey={algoliaConfig.apiKey}
          indexName={indexName}
          onSearchStateChange={this.onSearchStateChange}
          searchState={searchState}
        >
          {configurationDOM}
          {searchWidgetDOM}
        </InstantSearch>
      </div>
    );
  }
}

ProductDirectory.propTypes = {
  assets: PropTypes.shape({
    defaultOrganisationListingLogoUrl: PropTypes.string.isRequired
  }).isRequired,
  navigation: PropTypes.shape({
    exhibitorPublicDetailsUrlFormat: PropTypes.string.isRequired
  }).isRequired,
  context: PropTypes.shape({
    eventEditionId: PropTypes.string.isRequired,
    eventId: PropTypes.string.isRequired,
    interfaceLocale: PropTypes.string.isRequired,
    primaryLocale: PropTypes.string.isRequired,
    mode: PropTypes.oneOf([Modes.private, Modes.protected, Modes.public]),
    showPlanningEnabled: PropTypes.bool
  }).isRequired,
  algoliaConfig: PropTypes.shape({
    appId: PropTypes.string.isRequired,
    apiKey: PropTypes.string.isRequired
  }).isRequired,
  testVariants: PropTypes.arrayOf(PropTypes.string)
};

export default ProductDirectory;
